import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { ErrorService } from "@/lib/errors";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DefaultTitlePromptResponse {
  defaultTitlePrompt: string;
}

interface CabinetSettingsResponse {
  /** Dedicated column (migration 125) — moved out of formatting_settings jsonb. */
  piece_title_prompt?: string | null;
}

export interface PreviewNomenclatureResponse {
  title: string | null;
  description: string | null;
  documentDate: string | null;
  success: boolean;
  extractedText: string;
}

export interface PreviewTitleResponse {
  title: string | null;
  description: string | null;
  documentDate: string | null;
  success: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

/**
 * General key for the cabinet's saved piece-title prompt.
 *
 * Exported because this setting has a second writer — the agent's
 * `set_piece_naming_rules` tool — whose refresh is wired in
 * `sessionDoneBridge.ts`. A literal duplicated there would silently stop
 * matching the day this key is renamed.
 */
export const PIECE_TITLE_PROMPT_KEY = "cabinet-piece-title-prompt";

/** Cabinet-scoped key used by the query and its precise invalidation. */
export const pieceTitlePromptKey = (cabinetId: string | null) =>
  [PIECE_TITLE_PROMPT_KEY, cabinetId] as const;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Fetches the server-side default title-generation rules.
 * staleTime: Infinity because the default never changes at runtime.
 */
export function useDefaultTitlePrompt() {
  return useQuery({
    queryKey: ["nomenclature-default-prompt"],
    queryFn: () =>
      apiClient.get<DefaultTitlePromptResponse>("/nomenclature/default-prompt"),
    staleTime: Infinity,
  });
}

/**
 * Fetches the cabinet's saved custom piece-title prompt, if any.
 * Returns null when no custom prompt has been set (fallback = default).
 * A thrown error (e.g. 404 when cabinet has no settings row) is treated
 * as "no custom prompt" and returns null rather than surfacing an error.
 *
 * refetchOnMount:'always' ensures every mount issues a fresh network request, so
 * the editor never seeds from a cache that the agent has meanwhile invalidated
 * server-side. It is NOT sufficient on its own: since the assistant bar opens the
 * docked assistant instead of navigating away (2026-08-12), nothing unmounts this
 * screen any more. The refresh while mounted is driven by the invalidation in
 * `sessionDoneBridge.ts` and arbitrated by `resolveExternalDraftUpdate` in
 * NomenclatureSettingsTab.
 */
export function useCabinetPieceTitlePrompt(cabinetId: string | null) {
  return useQuery({
    queryKey: pieceTitlePromptKey(cabinetId),
    enabled: !!cabinetId,
    refetchOnMount: "always",
    queryFn: async (): Promise<string | null> => {
      try {
        const s = await apiClient.get<CabinetSettingsResponse>(
          "/cabinet-settings/" + cabinetId,
        );
        return s?.piece_title_prompt ?? null;
      } catch {
        // 404 or any error = cabinet has no settings yet → use default
        return null;
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Mutation
// ---------------------------------------------------------------------------

/**
 * Saves a custom piece-title prompt for the cabinet.
 * Invalidates both the scoped and general query keys so any other
 * consumers (e.g. future admin views) refetch.
 */
export function useSavePieceTitlePrompt(cabinetId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pieceTitlePrompt: string) => {
      if (!cabinetId) {
        throw new Error(
          "useSavePieceTitlePrompt: cabinetId is null — cannot PATCH cabinet settings",
        );
      }
      return apiClient.patch(
        "/cabinet-settings/" + cabinetId + "/piece-title-prompt",
        { pieceTitlePrompt },
      );
    },
    onSuccess: () => {
      toast.success("Nomenclature enregistrée");
      // Invalidate scoped key (re-fetches the saved value for this cabinet)
      // AND the general key (future list views or admin screens).
      queryClient.invalidateQueries({
        queryKey: pieceTitlePromptKey(cabinetId),
      });
      queryClient.invalidateQueries({
        queryKey: [PIECE_TITLE_PROMPT_KEY],
      });
    },
    onError: (error: unknown) => {
      ErrorService.handle(
        error instanceof Error ? error : new Error("Impossible d'enregistrer les règles de nommage"),
        {
          userMessage: "Les règles de nommage n'ont pas pu être enregistrées. Veuillez réessayer.",
          context: { action: "save_nomenclature_settings" },
        },
      );
    },
  });
}

// ---------------------------------------------------------------------------
// Plain async helpers (NOT hooks) for preview calls
// ---------------------------------------------------------------------------

/**
 * Sends a file to the backend for OCR + title generation.
 * Can take several seconds — callers are responsible for showing a loading state.
 */
export async function previewNomenclature(
  file: File,
  titleRules: string,
): Promise<PreviewNomenclatureResponse> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("titleRules", titleRules);
  return apiClient.post<PreviewNomenclatureResponse>(
    "/nomenclature/preview",
    fd,
  );
}

/**
 * Re-runs title generation from already-extracted text (no OCR, instant).
 * Used when the user tweaks the prompt after an initial preview drop.
 */
export async function previewNomenclatureTitle(
  extractedText: string,
  titleRules: string,
): Promise<PreviewTitleResponse> {
  return apiClient.post<PreviewTitleResponse>("/nomenclature/preview-title", {
    extractedText,
    titleRules,
  });
}

// NOTE: POST /nomenclature/rewrite-prompt and the useRewriteNomenclaturePrompt
// hook that called it are no longer consumed by the frontend (the inline AI
// panel was replaced by a Mémoire-style /assistant handoff in 2026-07-29).
// The backend route remains intact (backend policy: no deletions without
// explicit validation). File a cleanup issue if the route should be removed.
